"use client";

import React, { createContext, useContext, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePathname } from "next/navigation";

interface Carrier {
    id: string;
    name: string;
    slug: string;
    api_endpoint: string | null;
    // O valor da credencial não trafega mais para o cliente — só a existência.
    // Ver `lib/carrier-response.ts`. Nenhum destes componentes lia o valor.
    has_api_key?: boolean;
    has_api_secret?: boolean;
    tracking_url_template: string | null;
    external_carrier_id: string | null;
    code_prefixes: string[];
    is_active: boolean;
}

interface CarrierContextType {
    carriers: Carrier[];
    isLoading: boolean;
    error: any;
    refetch: () => void;
}

const CarrierContext = createContext<CarrierContextType | undefined>(undefined);

export function CarrierProvider({ children }: { children: ReactNode }) {
    const pathname = usePathname();
    const publicPages = ["/", "/login", "/register", "/lp2", "/onboarding"];
    const isPublicPage = publicPages.includes(pathname);

    const { data: carriersData, isLoading, error, refetch } = useQuery<{ data: Carrier[] }>({
        queryKey: ["carriers"],
        queryFn: async () => {
            const res = await fetch("/api/carriers");
            if (!res.ok) throw new Error("Failed to fetch carriers");
            return res.json();
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
        enabled: !isPublicPage, // Only fetch on authenticated pages
        retry: (failureCount, error) => {
            // Don't retry on 401/403 or after 3 attempts
            if (error.message.includes("403") || error.message.includes("401")) return false;
            return failureCount < 3;
        }
    });

    const carriers = carriersData?.data || [];

    return (
        <CarrierContext.Provider value={{ carriers, isLoading, error, refetch }}>
            {children}
        </CarrierContext.Provider>
    );
}

export function useCarriers() {
    const context = useContext(CarrierContext);
    if (context === undefined) {
        throw new Error("useCarriers must be used within a CarrierProvider");
    }
    return context;
}
